import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { useFragmentCreationStore } from '@app/modules/fragments/stores/fragment-creation.store';
import { CopyLinkAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/CopyLinkAtTimestampButton';
import { PlayAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/PlayAtTimestampButton';
import { getTopicDisplaySummary } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-summary';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { useOpenTopicDetailDrawer } from '@app/modules/logger/screens/stream/features/live-topics/hooks/useOpenTopicDetailDrawer';
import { LiveTopic } from '../../live-topics-types';
import { Timestamp, toTimestamp } from '@mi-front/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useOpenFragmentDrawer } from '../../../../components/focus/selection/useOpenFragmentDrawer';
import { useStreamSelection } from '../../../../stores/useStreamSelection';

const openInPanelMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.popup.openInPanel',
  defaultMessage: 'Ver más detalles',
});

const createFragmentMessage = defineMessage({
  id: 'logger.streamScreen.selectionMenu.createFragment',
  defaultMessage: 'Crear fragmento',
});

/**
 * Live topic popup footer actions only (play, create fragment, copy link, open in panel).
 * Divider and button row chrome are owned by StreamPopup's footer slot.
 */
export function LiveTopicPopupFooter({
  topic,
  onClose,
}: {
  topic: LiveTopic;
  onClose?: () => void;
}) {
  const { $t } = useIntl();
  const station = useActiveStation();
  const { setSelection } = useStreamSelection();
  const setFormValues = useFragmentCreationStore((s) => s.setFormValues);
  const openFragmentDrawer = useOpenFragmentDrawer();
  const openTopicDetailDrawer = useOpenTopicDetailDrawer();

  const hasStartTime = !!topic.start_time;
  const hasEndTime = !!topic.end_time;
  const canPlay = hasStartTime;
  const canCreateFragment = hasStartTime && hasEndTime;
  const startTs = (hasStartTime ? toTimestamp(topic.start_time!) : 0) as Timestamp;

  const handleCreateFragment = useCallback(() => {
    if (!canCreateFragment) {
      return;
    }

    const start = toTimestamp(topic.start_time!);
    const endTs = toTimestamp(topic.end_time!);
    setSelection(start, endTs);
    setFormValues(station.id, {
      title: getTopicDisplayTitle(topic),
      description: getTopicDisplaySummary(topic) ?? '',
    });
    onClose?.();
    openFragmentDrawer();
  }, [
    canCreateFragment,
    topic,
    setSelection,
    setFormValues,
    station.id,
    onClose,
    openFragmentDrawer,
  ]);

  const handleOpenInPanel = useCallback(() => {
    openTopicDetailDrawer(topic);
    onClose?.();
  }, [topic, openTopicDetailDrawer, onClose]);

  return (
    <>
      <PlayAtTimestampButton time={startTs} disabled={!canPlay} onDone={onClose} />
      <Tooltip title={$t(createFragmentMessage)} placement="top">
        <IconButton
          size="small"
          color="secondary"
          onClick={handleCreateFragment}
          disabled={!canCreateFragment}
        >
          <FragmentIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <CopyLinkAtTimestampButton time={startTs} disabled={!canPlay} onDone={onClose} />
      <Tooltip title={$t(openInPanelMessage)} placement="top">
        <IconButton size="small" color="secondary" onClick={handleOpenInPanel}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
}
